<template>
  <div>
    <announcement-alert />
    <whats-new-modal />
    <pn-onboarding-banner />
    <pending-documentation-modal />
    <franchise-manual-cycle-modal />
    <float-to-do-sidebar />
    <layout-vertical>
      <router-view />
      <app-customizer v-if="showCustomizer" slot="customizer" />
    </layout-vertical>
  </div>
</template>

<script>
import LayoutVertical from "@core/layouts/layout-vertical/LayoutVertical.vue";
import AppCustomizer from "@core/layouts/components/app-customizer/AppCustomizer.vue";
import AnnouncementAlert from "@/modules/account/views/AnnouncementAlert.vue";
import WhatsNewModal from "@/modules/whats-new/components/WhatsNewModal.vue";
import PnOnboardingBanner from "@/modules/consultant/views/PnOnboardingBanner.vue";
import PendingDocumentationModal from "@/modules/consultant/views/PendingDocumentationModal.vue";
import FranchiseManualCycleModal from "@/modules/franchise_manual/components/FranchiseManualCycleModal.vue";
import { $themeConfig } from "@themeConfig";
import FloatToDoSidebar from "@/modules/todo/components/FloatToDoSidebar.vue";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import education from "@/navigation/education";
import { get } from "lodash";
import reportLockAware from '@/mixins/reportLockAware'
import { isReportPendingError } from '@/helpers/error_messages'

export default {
  components: {
    AppCustomizer,
    LayoutVertical,
    AnnouncementAlert,
    WhatsNewModal,
    PnOnboardingBanner,
    PendingDocumentationModal,
    FranchiseManualCycleModal,
    FloatToDoSidebar,
  },
  mixins: [reportLockAware],
  data() {
    return {
      displayedNotificationIds: this.loadDisplayedIds(),
    };
  },
  computed: {
    ...mapGetters({
      notificationsRaw: sharedTypes.NOTIFICATIONS_LIST,
    }),
    showCustomizer() {
      return $themeConfig.layout.customizer;
    },
    filteredNotifications() {
      if (!this.notificationsRaw?.data) return [];

      return this.notificationsRaw.data
        .filter((n) => n.status === "visible" && !n.dismissed_at)
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, 3);
    },
  },
  setup() {
    return { toast: useToast() };
  },
  mounted() {
    // Com report pendente a API recusa estas rotas (423): carregam quando o acesso for liberado.
    this.runWhenReportUnlocked(() => {
      this.loadNotifications()
      this.loadBusinessSchoolColaboradorLink()
      this.loadCodexLink()
    })
  },
  methods: {
    ...mapActions({
      getNotificationsList: sharedTypes.GET_NOTIFICATIONS_LIST,
      patchNotification: sharedTypes.PATCH_NOTIFICATION,
      getBusinessSchoolColaboradorUrl:
        sharedTypes.GET_BUSINESS_CONFIG_BUSINESS_SCHOOL_COLABORADOR,
      getCodexUrl: sharedTypes.GET_BUSINESS_CONFIG_CODEX_URL,
    }),

    // Busca o link do submenu "Colaborador" (Futuro Academy) no business_config.
    // Enquanto o value estiver vazio, o item segue desabilitado ("Em breve").
    // Quando a URL for preenchida no banco, o submenu vira link clicável — sem deploy.
    async loadBusinessSchoolColaboradorLink() {
      try {
        const resp = await this.getBusinessSchoolColaboradorUrl();
        const url = get(resp, "data.value");
        if (!url) return;

        const item = (education[0]?.children || []).find(
          (child) => child.menuKey === "business-school-colaborador"
        );
        if (!item) return;

        item.href = url;
        item.disabled = false;
        item.tag = null;
      } catch (error) {
        // Sem link configurado / falha na busca — mantém o item desabilitado.
      }
    },

    async loadCodexLink() {
      try {
        const resp = await this.getCodexUrl();
        const url = get(resp, "data.value");
        if (!url) return;

        const item = (education[0]?.children || []).find(
          (child) => child.menuKey === "futuro-academy-codex"
        );
        if (!item) return;

        item.href = url;
        item.disabled = false;
        item.tag = null;
      } catch (error) {
        // Sem link configurado / falha na busca — mantém o item desabilitado.
      }
    },

    async loadNotifications() {
      try {
        await this.getNotificationsList();
        this.processNotifications();
      } catch (error) {
        // A trava chegou no meio do caminho (423): o PN já está sendo levado à central — sem toast de erro.
        if (isReportPendingError(error)) return
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as notificações. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    loadDisplayedIds() {
      try {
        const stored = localStorage.getItem("displayedPopupNotificationIds");
        return new Set(stored ? JSON.parse(stored) : []);
      } catch {
        return new Set();
      }
    },

    saveDisplayedIds() {
      try {
        localStorage.setItem(
          "displayedPopupNotificationIds",
          JSON.stringify([...this.displayedNotificationIds])
        );
      } catch {
        // localStorage indisponível — falha silenciosa, sem quebrar o fluxo
      }
    },

    processNotifications() {
      const newNotifications = this.filteredNotifications.filter(
        (notification) => !this.displayedNotificationIds.has(notification.id)
      );

      newNotifications.forEach((notification) => {
        this.showNotificationToast(notification);
        this.displayedNotificationIds.add(notification.id);
      });

      if (newNotifications.length > 0) {
        this.saveDisplayedIds();
      }
    },

    showNotificationToast(notification) {
      this.toast(
        {
          component: ToastificationContent,
          props: {
            title: notification.title,
            icon: notification.icon || "BellIcon",
            text: notification.body,
            variant: "info",
            hideClose: true
          },
        },
        {
          timeout: false,
          closeOnClick: false,
          position: "bottom-right",
          id: notification.id,
          closeButton: "button",
          onClick: () => this.handleNotificationClick(notification),
          onClose: () =>{
            this.patchNotification(notification.id);
          }
        }
      );
    },

    async handleNotificationClick(notification) {
      try {
        this.toast.dismiss(notification.id);
        await this.patchNotification(notification.id);

        if (notification.target_url) {
          if (this.isInternalUrl(notification.target_url)) {
            const path = new URL(notification.target_url).pathname;
            this.$router.push(path);
          } else {
            window.open(notification.target_url, "_blank");
          }
        }
      } catch (error) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao atualizar as notificações. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    isInternalUrl(url) {
      try {
        const target = new URL(url);
        const current = new URL(window.location.href);
        return target.hostname === current.hostname;
      } catch {
        return false;
      }
    },
  },
  watch: {
    filteredNotifications: {
      handler() {
        this.processNotifications();
      },
      deep: true,
    },
  },
};
</script>
